import { __decorate, __metadata } from "tslib";
import { Type } from '@ftr/serialization';
import { Playability, RecordingSegment } from '../../read';
import { isWithinTimecodes } from '../../shared/time-utils';
import { RecordingType } from '../../type/recording';
import { Timecode } from '../../type/shared';
import { TimeframeWithLocalTimes } from '../shared';
export class AudioSegmentPlaybackData {
    /**
     * @param id Identifies the AudioSegment
     * @param courtSystemId Identifies the court system the audio segment belongs to
     * @param timecodes Describes the time periods during which the audio contents was captured
     * @param shareStatus The share status of the audio segment
     * @param isLive Whether the audio segment is live, i.e. whether audio after the latest timecode start is accessible
     * @param recordingId Identifies the matching recording for the audio segment
     * @param recordingSegments Recording segment details needed for loudness and live streams
     * @param sealedSegments An array of non-overlapping timeframes that have been 'sealed'
     * @param hasVideo Whether the audio segment has video
     * @param hasMultiChannel Whether the audio segment has a multichannel playlist
     * @param audioChannelCount The number of audio channels in this segment
     * @param onTheRecordTimeframes Timeframes in which this audio segment's recording is on the record (Stream only)
     * @param availableTimeframes The precise local times that the user can access in this recording (Stream only)
     * @param playlistUrl The URL of the HLS playlist for the audio segment player, which could be hosted from Core or Regional-API
     * @param regionalLogSheetIds An array of regional log sheet ids for this audio segment
     * @param speechToTextIncluded Whether STT is included
     */
    constructor(id, courtSystemId, timecodes, shareStatus, isLive, recordingId, recordingSegments, sealedSegments, hasVideo, hasMultiChannel, audioChannelCount, onTheRecordTimeframes, availableTimeframes, playlistUrl, regionalLogSheetIds, speechToTextIncluded) {
        this.id = id;
        this.courtSystemId = courtSystemId;
        this.shareStatus = shareStatus;
        this.isLive = isLive;
        this.recordingId = recordingId;
        this.sealedSegments = sealedSegments;
        this.hasVideo = hasVideo;
        this.hasMultiChannel = hasMultiChannel;
        this.audioChannelCount = audioChannelCount;
        this.onTheRecordTimeframes = onTheRecordTimeframes;
        this.playlistUrl = playlistUrl;
        this.regionalLogSheetIds = regionalLogSheetIds;
        this.speechToTextIncluded = speechToTextIncluded;
        this.timecodes = timecodes;
        this.recordingSegments = recordingSegments;
        this.availableTimeframes = availableTimeframes;
    }
}
__decorate([
    Type(() => Timecode),
    __metadata("design:type", Array)
], AudioSegmentPlaybackData.prototype, "timecodes", void 0);
__decorate([
    Type(() => RecordingSegment),
    __metadata("design:type", Array)
], AudioSegmentPlaybackData.prototype, "recordingSegments", void 0);
__decorate([
    Type(() => TimeframeWithLocalTimes),
    __metadata("design:type", Array)
], AudioSegmentPlaybackData.prototype, "availableTimeframes", void 0);
export function mapToAudioSegmentPlaybackData(audioSegment, timecodes, recordingSegments, sealedSegments, hasVideo, onTheRecordTimeframes, availableTimeframes, recordingType, playlistUrlBase, regionalLogSheetIds) {
    const hasMultiChannel = recordingSegments[0].hasMultiChannel;
    let playlistUrl;
    if (recordingType === RecordingType.Stream) {
        playlistUrl = `${playlistUrlBase}/audio-segment/${audioSegment.id}/hls/playlist.m3u8`;
    }
    else {
        playlistUrl = hasMultiChannel
            ? `${playlistUrlBase}/audio-segment/${audioSegment.id}/multi-channel-playlist`
            : `${playlistUrlBase}/audio-segment/${audioSegment.id}/playlist`;
    }
    return new AudioSegmentPlaybackData(audioSegment.id, audioSegment.courtSystemId, timecodes, audioSegment.shareStatus, isAudioSegmentLive(audioSegment, recordingSegments), audioSegment.recordingId, recordingSegments, sealedSegments, hasVideo, hasMultiChannel, recordingSegments[0].audioChannelCount, onTheRecordTimeframes, availableTimeframes, playlistUrl, regionalLogSheetIds, audioSegment.speechToTextIncluded);
}
/**
 * Real-time order: timecodes are not always accurate, audioSegment.isLive is persisted in the core db
 * Audio order: isLive is not supported
 * Shared recording: timecodes are accurate, isLive is calculated from recording segment and audio segment timecodes
 *
 * @param audioSegment – audio segment summary in question
 * @param recordingSegments – recording segments
 */
export function isAudioSegmentLive(audioSegment, recordingSegments) {
    if (audioSegment.isLive) {
        return true;
    }
    const liveRecordingSegment = recordingSegments.find(s => s.playability === Playability.Live);
    if (!(liveRecordingSegment === null || liveRecordingSegment === void 0 ? void 0 : liveRecordingSegment.timecode)) {
        return false;
    }
    return isWithinTimecodes(liveRecordingSegment.timecode.end, audioSegment.timecodes);
}
